import { createServerFn } from "@tanstack/react-start";

import { authOrCoachMiddleware } from "~/lib/auth-middleware";
import { requireClerkCoachAuth } from "~/lib/coach-middleware";
import {
  CreateGoalSchema,
  GoalByEngagementSchema,
  GoalByIdSchema,
  SetProgramGoalDoneSchema,
  UpdateGoalSchema,
} from "~/lib/schemas/goal";
import { SetKeyGoalSchema } from "~/lib/schemas/goal-link";

export const getGoalMainByEngagementFn = createServerFn({ method: "POST" })
  .middleware([authOrCoachMiddleware])
  .validator(GoalByEngagementSchema)
  .handler(async ({ data, context }) => {
    const { checkEngagementByUserOrCoach } =
      await import("~/lib/server/engagement");
    const { getGoalMainByEngagement } = await import("~/lib/server/goal");

    await checkEngagementByUserOrCoach(
      data.engagementId,
      context.userId,
      context.coachId,
    );
    return getGoalMainByEngagement(data.engagementId);
  });

export const getAllGoalsFn = createServerFn({ method: "POST" })
  .middleware([authOrCoachMiddleware])
  .validator(GoalByEngagementSchema)
  .handler(async ({ data, context }) => {
    const { checkEngagementByUserOrCoach } =
      await import("~/lib/server/engagement");
    const { getAllGoals } = await import("~/lib/server/goal");

    const { participantType } = await checkEngagementByUserOrCoach(
      data.engagementId,
      context.userId,
      context.coachId,
    );
    return getAllGoals(
      data.engagementId,
      participantType === "user" ? "client" : "coach",
    );
  });

// Saves that write keys need the coach's signed-in account, which holds them.
export const updateGoalFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(UpdateGoalSchema)
  .handler(async ({ data, context }) => {
    const { updateGoal } = await import("~/lib/server/goal");

    return updateGoal(context.coach.id, data.id, {
      encryptedDescription: data.encryptedDescription,
      iv: data.iv,
      key: data.key,
      deadline: data.deadline,
      keyGrants: data.keyGrants,
    });
  });

export const createGoalFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(CreateGoalSchema)
  .handler(async ({ data, context }) => {
    const { createGoal } = await import("~/lib/server/goal");

    return createGoal(context.coach.id, {
      id: data.id,
      engagementId: data.engagementId,
      encryptedDescription: data.encryptedDescription,
      iv: data.iv,
      key: data.key,
      deadline: data.deadline,
      keyGrants: data.keyGrants,
      parentGoalId: data.parentGoalId,
    });
  });

export const deleteGoalFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(GoalByIdSchema)
  .handler(async ({ data, context }) => {
    const { deleteGoal } = await import("~/lib/server/goal");

    const coachId = context.coach.id;
    return deleteGoal(coachId, data.id);
  });

/** The program's coach or client marks a program goal done, or reopens it. */
export const setProgramGoalDoneFn = createServerFn({ method: "POST" })
  .middleware([authOrCoachMiddleware])
  .validator(SetProgramGoalDoneSchema)
  .handler(async ({ data, context }) => {
    const { setProgramGoalDone } = await import("~/lib/server/goal");

    await setProgramGoalDone(
      { userId: context.userId, coachId: context.coachId },
      data.id,
      data.done,
    );
  });

/** The coach makes a goal the program's key goal, or unsets it. */
export const setKeyGoalFn = createServerFn({ method: "POST" })
  .middleware([requireClerkCoachAuth])
  .validator(SetKeyGoalSchema)
  .handler(async ({ data, context }) => {
    const { setKeyGoal } = await import("~/lib/server/goal");
    await setKeyGoal(context.coach.id, data);
  });
